import { z } from 'zod';

import { EntityRootSchema, type EntityRoot } from './pack.js';

// crypto.randomUUID is available at runtime in modern browsers, Web
// Workers, Cloudflare Workers, and Node 19+ — we don't want to pull DOM
// lib types into every consumer just to type this one call.
declare const crypto: { randomUUID(): string };

/**
 * Entity payload as recorded in an op. At Walking Skeleton scope only
 * `entity.create` exists; later phases add `entity.attach_trait` and
 * `entity.archive` (when soft-delete on entities lands).
 *
 * Per the substrate invariant (B2): the entities-table view is derived
 * from these ops, not authoritative on its own. Rebuildable from op_log.
 */
export const EntityCreatePayloadSchema = z
  .object({
    kind: z.literal('entity.create'),
    root: EntityRootSchema,
    traits: z.array(z.string().min(1)).readonly().default([]),
  })
  .strict();
export type EntityCreatePayload = z.infer<typeof EntityCreatePayloadSchema>;

/** The in-memory shape of an entity, computed from its op chain. */
export interface Entity {
  id: string;
  root: EntityRoot;
  traits: readonly string[];
}

/**
 * Mint a new entity id. Format: `<root>_<random>` so the root is
 * recognisable in logs. Phase 1 may move to opaque UUIDs.
 */
export function mintEntityId(root: EntityRoot): string {
  return `${root}_${crypto.randomUUID()}`;
}

/**
 * Build a SubmittedOp payload for creating an entity. The substrate stores
 * it in the op log; the entities-table view is computed from this stream.
 */
export function entityCreatePayload(root: EntityRoot, traits: readonly string[] = []): EntityCreatePayload {
  return { kind: 'entity.create', root, traits };
}
